import React, { useState } from "react";
import styles from "./chat-suggestion.module.less";
import { useSelector, useDispatch } from "react-redux";
import { getShowSuggestion, getChatbotSuggestionList, getFilterChatbotSuggestionList, getChatInputValue, getShowSuggestinFlag } from "../../reducers/chat-suggestion-reducer";
import { USER_MSG } from "../../common/config";
import { ChatMsg, DetectIntentReq } from "../../types/chat-msg-types";
import { addChatMsgAction, sendMessageAction, suggestionInputAction } from "../../actions/chat-msg-actions";
import { showSuggestionAction, clearInputAction } from "../../actions/chat-suggestion-actions";
import { CommonConstant } from "../../common/CommonConstant";

const escapeRegExp = (value: string) => value.replaceAll(/([,.+?:()*\[\]^$|{}\\-])/g, "\\$1");

const highlightPrompt = (prompt: string, inputValue: string) => {
    const term = inputValue.trim();
    if (!term) {
        return prompt;
    }

    const regex = new RegExp(`(${escapeRegExp(term)})`, "gi");
    const parts = prompt.split(regex);

    return parts.map((part, idx) => {
        if (part.toLowerCase() === term.toLowerCase()) {
            return <span key={`${part}-${idx}`} className="matchedWords">{part}</span>;
        }
        return <React.Fragment key={`${part}-${idx}`}>{part}</React.Fragment>;
    });
};

const ChatSuggestion: React.FC = () => {
    const dispatch = useDispatch();
    const showSuggestion = useSelector(getShowSuggestion);
    const showSuggestionFlag = useSelector(getShowSuggestinFlag);
    const filterChatbotSuggestionList = useSelector(getFilterChatbotSuggestionList);
    const chatInputValue = useSelector(getChatInputValue);
    const [index, setIndex] = useState<number>(-1);
    const [userInput, setUserInput] = useState<string>("");



    const isValidFocusedIndex = (focused: number, maxIndex: number) => focused >= 0 && focused <= maxIndex;

    const updateFocusStorage = (isFocused: boolean) => {
        sessionStorage.setItem("suggestionFocused", isFocused ? "true" : "false");
        if (!isFocused) {
            sessionStorage.setItem("messageSending", "false");
        }
    };

    const setItemStyle = (items: HTMLCollectionOf<Element>, targetIndex: number, style: string) => {
        items.item(targetIndex)?.setAttribute("style", style);
    };

    const updateFocusedInput = (focused: number, maxIndex: number) => {
        if (isValidFocusedIndex(focused, maxIndex)) {
            sessionStorage.setItem("suggestionFocused", "true");
            dispatch(suggestionInputAction(filterChatbotSuggestionList[focused]?.prompts));
            return;
        }

        dispatch(suggestionInputAction(userInput));
        sessionStorage.setItem("suggestionFocused", "false");
    };

    const handleArrowUp = (items: HTMLCollectionOf<Element>, focused: number, maxIndex: number) => {
        if (isValidFocusedIndex(focused, maxIndex)) {
            setItemStyle(items, focused, "background-color : rgb(255,255,255); ");
        } else {
            setUserInput(chatInputValue);
        }

        const nextFocused = focused - 1 < -1 ? maxIndex : focused - 1;
        if (isValidFocusedIndex(nextFocused, maxIndex)) {
            setItemStyle(items, nextFocused, "background-color : rgb(235,239,241);");
        }
        updateFocusedInput(nextFocused, maxIndex);
        setIndex(nextFocused);
    };

    const handleArrowDown = (items: HTMLCollectionOf<Element>, focused: number, maxIndex: number) => {
        if (isValidFocusedIndex(index, maxIndex)) {
            setItemStyle(items, focused, "background-color : rgb(255,255,255);");
        } else {
            setUserInput(chatInputValue);
        }

        const nextFocused = focused + 1 > maxIndex ? -1 : focused + 1;
        if (isValidFocusedIndex(nextFocused, maxIndex)) {
            setItemStyle(items, nextFocused, "background-color : rgb(235,239,241);");
        }
        updateFocusedInput(nextFocused, maxIndex);
        setIndex(nextFocused);
    };

    const handleEnter = (focused: number, maxIndex: number) => {
        if (!isValidFocusedIndex(focused, maxIndex)) {
            sessionStorage.setItem("messageSending", "false");
            setIndex(-1);
            return;
        }

        const sending = sessionStorage.getItem("messageSending");
        if (sending === "true") {
            sessionStorage.setItem("messageSending", "false");
            setIndex(-1);
            return;
        }

        sessionStorage.setItem("messageSending", "true");
        sendSuggestion(filterChatbotSuggestionList[focused]?.prompts, filterChatbotSuggestionList[focused]?.intentCode, filterChatbotSuggestionList[focused]?.intentLanguage);
        sessionStorage.setItem("suggestionFocused", "false");
        setIndex(-1);
    };

    const resetSuggestionFocus = () => {
        setIndex(-1);
        sessionStorage.setItem("suggestionFocused", "false");
    };

    document.onkeydown = function (e) {
        const items = document.getElementsByClassName("suggestion-item");
        const focused = index;
        const maxIndex = items.length - 1;

        updateFocusStorage(isValidFocusedIndex(focused, maxIndex));

        if (!items) {
            resetSuggestionFocus();
            return;
        }

        switch (e.keyCode) {
            case 38:
                handleArrowUp(items, focused, maxIndex);
                break;
            case 40:
                handleArrowDown(items, focused, maxIndex);
                break;
            case 13:
                handleEnter(focused, maxIndex);
                break;
            default:
                resetSuggestionFocus();
        }
    }
    const sendSuggestion = (e: any, intentCode: string,language:string) => {
        setIndex(-1);
        // console.log("send suggestion intentCode",intentCode);
        const userMsg: ChatMsg = {msg: e, type: USER_MSG};
        let intentCodeString="query-string-"+intentCode;
        const sendMsg: DetectIntentReq = {message: intentCodeString, name: e, suggestion: true,language:language};
        dispatch(addChatMsgAction(userMsg));
        dispatch(sendMessageAction(sendMsg));
        dispatch(showSuggestionAction(false, []));
        dispatch(clearInputAction(true));
    };

    return (
        <div className={styles.ChatSuggestionLess} hidden={!showSuggestion || !showSuggestionFlag} data-testid="chat-suggestion">
            <div className="suggestion-title">Suggestion</div>
            <div className="suggestion-content">
                {
                    (filterChatbotSuggestionList && filterChatbotSuggestionList.length > 0) &&
                    filterChatbotSuggestionList.slice(0, 5).map((item, index) => {
                        const prompt = highlightPrompt(item.prompts, chatInputValue);
                        return <button type="button" className="suggestion-item" key={index} data-testid="suggestion-item" onClick={() => {
                            sendSuggestion(item.prompts,item.intentCode,item.intentLanguage)
                        }}>{prompt}</button>
                    })
                }
            </div>
            <div className="suggestion-line"></div>
        </div>
    )
}

export default ChatSuggestion;